import { FC, useEffect, useRef } from 'react'

import {
  ContextMenu,
  ContextMenuPlacements,
  ContextMenuTriggerOnActions
} from '@inno-ui/context-menu'
import { Folder } from '@inno-ui/file-icons'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarLineIcon,
  CodeIcon,
  DocumentLineIcon,
  EyeLineIcon,
  EyeSlashLineIcon,
  GearLineIcon,
  Grid3LineIcon,
  PhotoLineIcon,
  PlusIcon,
  PlusSquareOnSquareLineIcon,
  RectangleHorizontalLineIcon,
  TextformatTextIcon
} from '@inno-ui/icons'
import { hasAppAccess } from 'modules/app-registry/hooks/hasAppAccess'
import { useCreateFolder } from 'modules/desktop/modules/files-integration/hooks'
import { useOpenWindowMarketplace } from 'modules/desktop/pages/MainPage/hooks/useOpenWindowMarketplace'
import { useTranslation } from 'react-i18next'
import {
  useGetAppsQuery,
  useSetWidgetBarLocationMutation
} from 'shared/api/containerApi/requests'
import {
  AGENT_CHAT_WIDGET_HIDE_DURATIONS_MS,
  LocalStorageKey
} from 'shared/constants/localStorage'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import { useUserContext } from 'shared/hooks/useUserContext'
import { useDesktopUtilityWindowsStore } from 'shared/stores/desktopUtilityWindowsStore'
import type { DesktopGridItem } from 'shared/types/desktop-grid'
import { dispatchStorageEvent } from 'shared/utils/customEvents'
import { isProd } from 'shared/utils/isProd'
import { isTesting } from 'shared/utils/isTesting'
import { lsRemoveItem, lsSetItem } from 'shared/utils/local-storage'

import {
  useDesktopStore,
  useDevDrawerStore,
  useShortcutGroupStore,
  useSidebarWidgetsStore
} from '../../../../stores'
import { useDesktopContextMenuState } from '../../DesktopContextMenuContext'
import {
  AutoArrangeHandler,
  arrangeItemsByDate,
  arrangeItemsByName,
  arrangeItemsByType,
  arrangeItemsWithShortcutGroups
} from '../../utils'
import {
  clearNonProfileMemory,
  resolveLayoutMode,
  setLayoutMode,
  updateBaseLayout
} from '../../utils/reflowItemsForGridResize'
import styles from './DesktopGridWrapper.module.scss'
import { DesktopGridWrapperProps } from './DesktopGridWrapper.types'

export const DesktopGridWrapper: FC<DesktopGridWrapperProps> = ({
  children
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.context_menu'
  })
  const items = useDesktopStore(state => state.items)
  const setItems = useDesktopStore(state => state.setItems)
  const gridSize = useDesktopStore(state => state.gridSize)
  const shortcutGroups = useShortcutGroupStore(state => state.groups)
  const isDevDrawerVisible = useDevDrawerStore(state => state.isVisible)
  const toggleDevDrawer = useDevDrawerStore(state => state.toggle)
  const openDesktopUtilityWindow = useDesktopUtilityWindowsStore(
    state => state.openWindow
  )
  const { user } = useUserContext()
  const createFolder = useCreateFolder()
  const openWidgetMarketplace = useOpenWindowMarketplace()
  const widgetsPlacement = useSidebarWidgetsStore(state => state.placement)
  const setWidgetsPlacement = useSidebarWidgetsStore(
    state => state.setPlacement
  )
  const setWidgetBarLocationMutation = useSetWidgetBarLocationMutation()
  const { isContextMenuOpen, setIsContextMenuOpen } =
    useDesktopContextMenuState()
  const [isAgentWidgetVisible, setIsAgentWidgetVisible] = useLocalStorage(
    LocalStorageKey.AGENT_CHAT_WIDGET_VISIBLE,
    true
  )
  const { data: containerAppsData = [] } = useGetAppsQuery()

  // Latest right-click coords, so "New > Folder" places the placeholder there.
  const lastRightClickPointRef = useRef<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      lastRightClickPointRef.current = { x: e.clientX, y: e.clientY }
    }
    document.addEventListener('contextmenu', handler, true)
    return () => document.removeEventListener('contextmenu', handler, true)
  }, [])

  const handleViewWallpapers = () => {
    openDesktopUtilityWindow('wallpaper')
  }
  const handleViewPreferences = () => {
    openDesktopUtilityWindow('preferences')
  }
  const handleViewLogViewer = () => {
    openDesktopUtilityWindow('log-viewer')
  }

  const hideWidget = (durationMs: number) => {
    // No useLocalStorage hook for AGENT_CHAT_WIDGET_HIDE_EXPIRY — this component
    // only writes/clears it and never reads it, so a reactive hook would add
    // useState + event listeners with no benefit + additional complexity.
    lsSetItem(
      LocalStorageKey.AGENT_CHAT_WIDGET_HIDE_EXPIRY,
      String(Date.now() + durationMs)
    )
    setIsAgentWidgetVisible(false)
    dispatchStorageEvent(LocalStorageKey.AGENT_CHAT_WIDGET_VISIBLE, 'false')
  }

  const showWidget = () => {
    lsRemoveItem(LocalStorageKey.AGENT_CHAT_WIDGET_HIDE_EXPIRY)
    setIsAgentWidgetVisible(true)
    dispatchStorageEvent(LocalStorageKey.AGENT_CHAT_WIDGET_VISIBLE, 'true')
  }

  const handleWidgetPlacementChange = (placement: 'left' | 'right') => {
    setWidgetsPlacement(placement)
    setWidgetBarLocationMutation.mutate({ location: placement })
  }

  const createAutoArrangeHandler = (arranger: AutoArrangeHandler) => {
    return () => {
      // Mode follows the device class (viewport) — see useBaseLayout.
      setLayoutMode(resolveLayoutMode(window.innerWidth))

      setItems((prevItems: DesktopGridItem[]) => {
        const arranged = arrangeItemsWithShortcutGroups(
          arranger,
          prevItems,
          gridSize.cols,
          gridSize.rows,
          shortcutGroups
        )
        clearNonProfileMemory()
        updateBaseLayout(arranged, gridSize.cols, gridSize.rows)
        return arranged
      })
    }
  }

  return (
    <ContextMenu
      isOpen={isContextMenuOpen}
      setIsOpen={setIsContextMenuOpen}
      triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
      showAtClickPoint
      placement={ContextMenuPlacements.Right}
      triggerWrapperClassName={styles.triggerWrapper}
      usePortal
      maxHeight='100%'
      zIndex={9999}
      options={[
        {
          icon: PlusIcon,
          label: t('new.label'),
          options: [
            {
              fileIcon: Folder,
              label: t('new.folder'),
              onClick: () => createFolder(lastRightClickPointRef.current)
            }
          ]
        },
        {
          icon: PlusSquareOnSquareLineIcon,
          label: t('add_widget'),
          onClick: () => openWidgetMarketplace()
        },
        {
          icon: PhotoLineIcon,
          label: t('change_wallpaper'),
          onClick: handleViewWallpapers
        },
        // {
        //   icon: SquareLineIcon,
        //   label: t('big_icons')
        // },
        {
          icon: Grid3LineIcon,
          label: t('auto_arrange.label'),
          disabled: items.length === 0,
          options: [
            {
              label: t('auto_arrange.by_name'),
              onClick: createAutoArrangeHandler(arrangeItemsByName),
              icon: TextformatTextIcon
            },
            {
              label: t('auto_arrange.by_date'),
              onClick: createAutoArrangeHandler(arrangeItemsByDate),
              icon: CalendarLineIcon
            },
            {
              label: t('auto_arrange.by_type'),
              onClick: createAutoArrangeHandler(arrangeItemsByType),
              icon: DocumentLineIcon
            }
          ]
        },
        {
          icon: RectangleHorizontalLineIcon,
          label: t('widget_arrangement.label'),
          options: [
            {
              label: t('widget_arrangement.left'),
              icon: ArrowLeftIcon,
              isSelected: widgetsPlacement === 'left',
              onClick: () => {
                handleWidgetPlacementChange('left')
              }
            },
            {
              label: t('widget_arrangement.right'),
              icon: ArrowRightIcon,
              isSelected: widgetsPlacement === 'right',
              onClick: () => {
                handleWidgetPlacementChange('right')
              }
            }
          ]
        },
        {
          icon: GearLineIcon,
          label: t('preferences'),
          onClick: handleViewPreferences
        },
        ...(hasAppAccess('agent', containerAppsData)
          ? [
              isAgentWidgetVisible
                ? {
                    icon: EyeSlashLineIcon,
                    label: t('hide_ai_widget.label'),
                    options: (
                      Object.entries(AGENT_CHAT_WIDGET_HIDE_DURATIONS_MS) as [
                        keyof typeof AGENT_CHAT_WIDGET_HIDE_DURATIONS_MS,
                        number
                      ][]
                    ).map(([key, ms]) => ({
                      icon: CalendarLineIcon,
                      label: t(`hide_ai_widget.${key}`),
                      onClick: () => hideWidget(ms)
                    }))
                  }
                : {
                    icon: EyeLineIcon,
                    label: t('show_ai_widget'),
                    onClick: showWidget
                  }
            ]
          : []),
        // TODO: we need to replace role id with a permission
        ...(isTesting() ||
        user?.user_roles?.includes?.('b8a869fb-7aae-4085-927a-c2553a9cd271')
          ? [
              {
                icon: EyeLineIcon,
                label: t('log_viewer'),
                onClick: handleViewLogViewer
              }
            ]
          : []),
        // {
        //   isSeparator: true
        // },
        // {
        //   icon: PlusIcon,
        //   label: t('new_file')
        // }
        ...(!isProd()
          ? [
              { isSeparator: true },
              {
                icon: CodeIcon,
                label: t('dev_tools'),
                isSelected: isDevDrawerVisible,
                onClick: toggleDevDrawer
              }
            ]
          : [])
      ]}
      triggerNode={children}
    />
  )
}
